// OTEL Tracing Enhanced - 2026-02-05
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';

// ---- Configuration ----
// Traces are sent through the backend API gateway which proxies to the HTTP OTEL collector.
// This avoids Mixed Content issues (HTTPS frontend -> HTTP collector is blocked by browsers).
// Frontend -> Backend (HTTPS) -> OTEL Collector (HTTP)
const API_BASE_URL = process.env.APP_BASE_URL || '';
const SERVICE_NAME = process.env.VITE_OTEL_SERVICE_NAME || 'xspaces-frontend';
const ENVIRONMENT = process.env.NODE_ENV || import.meta.env.MODE || 'development';

// Use the backend API gateway as proxy for OTEL (routes: /otel/v1/traces, /otel/v1/logs)
const TRACE_ENDPOINT = `${API_BASE_URL}/otel/v1/traces`;
const LOGS_ENDPOINT = `${API_BASE_URL}/otel/v1/logs`;


// ---- Resource Attributes ----
const resource = resourceFromAttributes({
  'service.name': SERVICE_NAME,
  'deployment.environment': ENVIRONMENT,
});

// ---- Traces ----
const traceExporter = new OTLPTraceExporter({
  url: TRACE_ENDPOINT,
});

const traceProvider = new WebTracerProvider({
  resource,
  spanProcessors: [
    new BatchSpanProcessor(traceExporter)
  ],
});

traceProvider.register({
  contextManager: new ZoneContextManager(),
});

// ---- Logs ----
const logExporter = new OTLPLogExporter({
  url: LOGS_ENDPOINT,
});

const loggerProvider = new LoggerProvider({
  resource,
  processors: [
    new BatchLogRecordProcessor(logExporter),
  ],
});

export const logger = loggerProvider.getLogger('frontend-logger');

// ---- Instrumentations ----
registerInstrumentations({
  instrumentations: [
    // Track user interactions (clicks, drags)
    new UserInteractionInstrumentation({
      eventNames: ['click', 'drag']
    }),
    // Track page load performance
    new DocumentLoadInstrumentation(),
    // Track fetch API calls
    new FetchInstrumentation({
      // Propagate trace context to backend services
      propagateTraceHeaderCorsUrls: [
        /.*thedigitalgroup\.com.*/,
        /localhost.*/,
      ],
      // Clear timing resources to avoid memory leaks
      clearTimingResources: true,
    }),
    // Track XMLHttpRequest calls
    new XMLHttpRequestInstrumentation({
      propagateTraceHeaderCorsUrls: [
        /.*thedigitalgroup\.com.*/,
        /localhost.*/,
      ],
    }),
  ],
});

// Log initialization in development
if (ENVIRONMENT === 'development') {
  console.log(`[OTEL] Frontend tracing initialized: ${SERVICE_NAME} -> ${TRACE_ENDPOINT}`);
}
